<script>
import { mapStores } from 'pinia'

import api from '@/cartapi.js'
import { useCartStore } from '@/cart/stores/cart'
import CartEventHero from '@/cart/components/CartEventHero.vue'
import OrderSummaryCard from '@/cart/components/OrderSummaryCard.vue'
import { hasValidPricing } from '@/cart/utils/pricing.js'
import { parseLocalDate } from '@/utils'

export default {
  name: 'ConfigureLockerView',

  components: {
    CartEventHero,
    OrderSummaryCard,
  },

  props: {
    eventSlug: {
      type: String,
      required: true,
    },
    locationSlug: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      submitting: false,
      error: null,
      eventEnded: false,
      unavailableDates: [], // Dates that failed due to inventory
      quantity: 1,
      selectedOfferingKey: null,
      selectedOptionId: null,
      selectedDates: [],
      // Server-calculated pricing preview (accurate total)
      pricingPreview: null,
      loadingPreview: false,
    }
  },

  computed: {
    ...mapStores(useCartStore),

    locationName() {
      // Look up display name from locationsMeta using slug as ID
      const slug = this.locationSlug || ''
      const locationsMeta = this.cartStore.locationsMeta || []
      const location = locationsMeta.find((loc) => loc.id === slug)
      if (location && location.name) {
        return location.name
      }
      // Fallback: convert slug to title case
      return slug
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    },

    currentEvent() {
      return this.cartStore.selectedEvent
    },

    // Get offerings for this location from store
    locationOfferings() {
      const byLoc = this.cartStore.byLocation || {}
      for (const [locName, offerings] of Object.entries(byLoc)) {
        const locSlug = locName.toLowerCase().replace(/\s+/g, '-')
        if (locSlug === this.locationSlug) {
          return offerings
        }
      }
      return []
    },

    selectedOffering() {
      if (!this.selectedOfferingKey) return this.locationOfferings[0] || null
      return this.locationOfferings.find((o) => o.key === this.selectedOfferingKey) || null
    },

    // Check if the selected offering is available for purchase
    isAvailableForPurchase() {
      // Default to true if not specified (backwards compatibility)
      return this.selectedOffering?.availableForPurchase !== false
    },

    availableOptions() {
      return this.selectedOffering?.rentalOptions || []
    },

    selectedOption() {
      // If we have options, return based on ID or default to first
      if (this.availableOptions.length > 0) {
        if (this.selectedOptionId) {
          return this.availableOptions.find((o) => o.id === this.selectedOptionId)
        }
        return this.availableOptions[0]
      }
      return null
    },

    availableDates() {
      // Base dates from product or event
      let dates = []

      // Use product availableDates if present (highest precedence source of truth for inventory)
      if (
        this.selectedOffering &&
        this.selectedOffering.availableDates &&
        this.selectedOffering.availableDates.length > 0
      ) {
        dates = this.selectedOffering.availableDates
      } else if (this.currentEvent && this.currentEvent.startDate && this.currentEvent.endDate) {
        // Fallback generation
        const start = parseLocalDate(this.currentEvent.startDate)
        const end = parseLocalDate(this.currentEvent.endDate)
        if (start && end) {
          let curr = new Date(start)
          while (curr <= end) {
            const yyyy = curr.getFullYear()
            const mm = String(curr.getMonth() + 1).padStart(2, '0')
            const dd = String(curr.getDate()).padStart(2, '0')
            dates.push(`${yyyy}-${mm}-${dd}`)
            curr.setDate(curr.getDate() + 1)
          }
        }
      }

      // If Daily option has specific restricted dates, filter/use them
      if (
        this.pricingMode === 'daily' &&
        this.selectedOption &&
        this.selectedOption.availableDates
      ) {
        // If option restricts dates, valid dates are intersection
        const optDates = this.selectedOption.availableDates
        if (optDates.length > 0) {
          // Optimization: if base dates empty, use optDates?
          // No, product availableDates is master.
          // Assuming product.availableDates contains ALL dates, and option might restrict?
          // Actually, backend Phase 2 says "If option_id == daily, dates must be subset".
          // So we should intersect or use option dates if provided.
          // If product dates generated (fallback), treat as all available.
          if (dates.length === 0) return optDates
          return dates.filter((d) => optDates.includes(d))
        }
      }

      return dates
    },

    // Price per day or per event (in cents)
    // Returns null if consumerPriceCents is missing (triggers error state)
    unitPriceCents() {
      if (this.selectedOption) {
        if (!hasValidPricing(this.selectedOption)) {
          console.error('[Pricing Error] Missing consumerPriceCents on selectedOption')
          return null
        }
        return this.selectedOption.consumerPriceCents
      }
      if (this.selectedOffering?.consumerPriceCents && this.selectedOffering.consumerPriceCents > 0) {
        return this.selectedOffering.consumerPriceCents
      }
      console.error('[Pricing Error] Missing consumerPriceCents on selectedOffering')
      return null
    },

    // Check if current selection has a pricing error
    hasPricingError() {
      if (!this.selectedOption && !this.selectedOffering) {
        return false // No selection yet
      }
      return this.unitPriceCents === null
    },

    unitServiceFeeCents() {
      if (this.selectedOption) {
        return this.selectedOption.serviceFeeCents || 0
      }
      return this.selectedOffering?.serviceFeeCents || 0
    },

    totalServiceFeeCents() {
      if (this.pricingMode === 'fixed') {
        return this.unitServiceFeeCents * this.quantity
      }
      // Daily mode
      const numDays = this.selectedDates.length
      return this.unitServiceFeeCents * numDays * this.quantity
    },

    // Compatibility shim
    dailyRateCents() {
      return this.unitPriceCents
    },

    pricingMode() {
      if (this.selectedOption) return this.selectedOption.pricingMode
      return this.selectedOffering?.pricingMode || 'daily'
    },

    // Total in cents (returns 0 if pricing error to prevent NaN display)
    totalPriceCents() {
      const price = this.unitPriceCents
      if (price === null) return 0
      if (this.pricingMode === 'fixed') {
        return price * this.quantity
      }
      // Daily mode
      const numDays = this.selectedDates.length
      return price * numDays * this.quantity
    },

    datesLabel() {
      const count = this.selectedDates.length
      if (count === 0) return 'No dates selected'
      if (count === 1) return '1 day'
      return `${count} days`
    },

    canContinue() {
      return (
        this.selectedOffering &&
        this.isAvailableForPurchase &&
        this.selectedDates.length > 0 &&
        this.quantity > 0 &&
        !this.hasPricingError
      )
    },

    // Use server-calculated total when available, fallback to client calculation
    displayTotalCents() {
      if (this.pricingPreview) {
        return this.pricingPreview.displaySubtotalCents
      }
      return this.totalPriceCents
    },

    displayServiceFeeCents() {
      if (this.pricingPreview) {
        return this.pricingPreview.serviceFeeCents
      }
      return this.totalServiceFeeCents
    },
  },

  watch: {
    // Watch for changes that affect pricing and fetch preview
    selectedOfferingKey() {
      this.fetchPricingPreview()
    },
    selectedOptionId() {
      this.fetchPricingPreview()
    },
    selectedDates: {
      handler() {
        this.fetchPricingPreview()
      },
      deep: true,
    },
    quantity() {
      this.fetchPricingPreview()
    },
  },

  async created() {
    try {
      // If we don't have products yet, fetch them
      if (!this.cartStore.products || this.cartStore.products.length === 0) {
        const previewToken = this.$route.query.preview || null
        const data = await api.catalog.listProducts(this.eventSlug, previewToken)
        this.cartStore.setSelectedEvent(data.event)
        this.cartStore.setProducts(data)
      }

      // Verify event is purchasable before showing configure form (skip in preview mode)
      const event = this.cartStore.selectedEvent
      if (!this.cartStore.isPreviewMode && event && event.purchasable === false) {
        this.eventEnded = true
        this.loading = false
        return
      }

      // Auto-select first offering if available
      if (this.locationOfferings.length > 0 && !this.selectedOfferingKey) {
        this.selectOffering(this.locationOfferings[0].key)
      }

      // Auto-select option if only one
      if (this.availableOptions.length === 1) {
        this.selectedOptionId = this.availableOptions[0].id
      } else if (this.availableOptions.length > 1 && !this.selectedOptionId) {
        // Default to first (usually full_event if sorted, or daily)
        this.selectedOptionId = this.availableOptions[0].id
      }

      this.updateDateSelection()
    } catch (err) {
      console.error('Failed to load offerings', err)
      this.error = 'Unable to load offerings. Please try again.'
    } finally {
      this.loading = false
    }
  },

  methods: {
    async fetchPricingPreview() {
      // Don't fetch if we don't have required data
      if (!this.selectedOffering || !this.selectedOption || this.selectedDates.length === 0) {
        this.pricingPreview = null
        return
      }

      // Debounce: cancel previous pending request
      if (this._previewTimeout) {
        clearTimeout(this._previewTimeout)
      }

      this._previewTimeout = setTimeout(async () => {
        this.loadingPreview = true
        try {
          const preview = await api.orders.preview(
            this.selectedOffering.key,
            this.selectedOptionId,
            this.selectedDates,
            this.quantity
          )
          this.pricingPreview = preview
        } catch (err) {
          console.error('Failed to fetch pricing preview:', err)
          // Clear preview on error - will fall back to client calculation
          this.pricingPreview = null
        } finally {
          this.loadingPreview = false
        }
      }, 300) // 300ms debounce
    },

    selectOffering(offeringKey) {
      this.selectedOfferingKey = offeringKey
      this.error = null
      this.unavailableDates = []

      // Reset option and auto-select first available
      this.selectedOptionId = null
      const offering = this.locationOfferings.find((o) => o.key === offeringKey)
      if (offering?.rentalOptions?.length >= 1) {
        this.selectedOptionId = offering.rentalOptions[0].id
      }

      this.updateDateSelection()
    },

    selectOption(optionId) {
      this.selectedOptionId = optionId
      this.error = null
      this.updateDateSelection()
    },

    updateDateSelection() {
      const dates = this.availableDates

      if (this.pricingMode === 'fixed') {
        // Fixed price (Full Event) - select all dates automatically
        this.selectedDates = [...dates]
      } else {
        // Daily price - auto-select only if few dates (<= 4), excluding sold-out dates
        if (dates.length <= 4) {
          this.selectedDates = dates.filter((d) => !this.isDateUnavailable(d))
        } else {
          this.selectedDates = []
        }
      }
    },

    getDisplayPrice(offering) {
      // Use consumer price (all-in with fees) for display
      // Returns null if any option is missing valid pricing
      if (offering.rentalOptions && offering.rentalOptions.length) {
        const validPrices = offering.rentalOptions
          .filter((o) => hasValidPricing(o))
          .map((o) => o.consumerPriceCents)

        if (validPrices.length !== offering.rentalOptions.length) {
          console.error('[Pricing Error] Some options missing consumerPriceCents', offering.displayName)
          return null
        }
        return Math.min(...validPrices)
      }

      if (!offering.consumerPriceCents || offering.consumerPriceCents <= 0) {
        console.error('[Pricing Error] Missing consumerPriceCents on offering', offering.displayName)
        return null
      }
      return offering.consumerPriceCents
    },

    hasMultipleOptions(offering) {
      return offering.rentalOptions && offering.rentalOptions.length > 1
    },

    isOfferingPurchasable(offering) {
      return offering.availableForPurchase !== false
    },

    isSoldOut(offering) {
      // Check both explicit state and inventory availability
      return offering.state === 'sold_out' || offering.isAvailable === false
    },

    getDisplayModeLabel(offering) {
      const mode = offering.rentalOptions?.[0]?.pricingMode || offering.pricingMode || 'daily'
      return mode === 'fixed' ? ' /event' : ' /day'
    },

    getAvailabilityLabel(option) {
      if (this.isOptionEffectivelySoldOut(option)) return 'Sold Out'
      if (option.availability === 'sold_out' && option.dateAvailability) {
        // Some dates still available — show a scarcity hint instead of "Sold Out"
        return 'Limited Dates'
      }
      if (option.availability === 'almost_gone') return 'Almost Gone!'
      if (option.availability === 'limited') return 'Limited'
      return null
    },

    /**
     * A daily option is only truly sold out if every date in dateAvailability
     * is 'sold_out'. When dateAvailability is absent, fall back to the
     * top-level availability field.
     */
    isOptionEffectivelySoldOut(option) {
      if (option.pricingMode === 'daily' && option.dateAvailability) {
        const statuses = Object.values(option.dateAvailability)
        // Only sold out if every date in availableDates is accounted for and sold out.
        // dateAvailability only contains noteworthy dates (sold_out, almost_gone, limited),
        // so dates with plenty of stock won't appear in the map.
        const totalDates = option.availableDates?.length || 0
        return totalDates > 0 && statuses.length >= totalDates && statuses.every((s) => s === 'sold_out')
      }
      return option.availability === 'sold_out'
    },

    getBasePrice(offering) {
      // Get base price (vendor's price before fees)
      if (offering.rentalOptions && offering.rentalOptions.length) {
        const prices = offering.rentalOptions
          .map((o) => o.priceCents)
          .filter((p) => p !== undefined)
        return prices.length ? Math.min(...prices) : 0
      }
      return offering.priceCents || offering.dailyRateCents || 0
    },

    getServiceFee(offering) {
      // Get service fee (platform + Stripe fee)
      if (offering.rentalOptions && offering.rentalOptions.length) {
        const fees = offering.rentalOptions
          .map((o) => o.serviceFeeCents || 0)
          .filter((f) => f !== undefined)
        return fees.length ? Math.min(...fees) : 0
      }
      return offering.serviceFeeCents || 0
    },

    getOptionDisplayPrice(opt) {
      // Get display price for a rental option
      // Returns null if consumerPriceCents is missing
      if (!hasValidPricing(opt)) {
        console.error('[Pricing Error] Missing consumerPriceCents on rental option', opt.id)
        return null
      }
      return opt.consumerPriceCents
    },

    toggleDate(dateStr) {
      // Clear error when user modifies selection
      this.error = null

      const idx = this.selectedDates.indexOf(dateStr)
      if (idx >= 0) {
        this.selectedDates.splice(idx, 1)
      } else {
        this.selectedDates.push(dateStr)
        this.selectedDates.sort()
      }

      // Remove from unavailable if user deselects it
      const unavailIdx = this.unavailableDates.indexOf(dateStr)
      if (unavailIdx >= 0) {
        this.unavailableDates.splice(unavailIdx, 1)
      }
    },

    isDateUnavailable(dateStr) {
      return this.unavailableDates.includes(dateStr) || this.getDateAvailability(dateStr) === 'sold_out'
    },

    isDateSelected(dateStr) {
      return this.selectedDates.includes(dateStr)
    },

    formatDate(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    },

    formatDayAbbrev(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3)
    },

    formatDateNum(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.getDate()
    },

    getDateAvailability(dateStr) {
      const opt = this.selectedOption
      if (!opt || !opt.dateAvailability) return null
      return opt.dateAvailability[dateStr] || null
    },

    getDateScarcityLabel(dateStr) {
      const avail = this.getDateAvailability(dateStr)
      if (avail === 'sold_out') return 'Sold'
      if (avail === 'almost_gone') return 'Low'
      if (avail === 'limited') return 'Limited'
      return null
    },

    async continueToCheckout() {
      if (!this.canContinue || this.submitting) return

      this.submitting = true
      this.error = null

      try {
        let datesToSubmit = [...this.selectedDates]

        // Ensure we have dates
        if (datesToSubmit.length === 0) {
          this.error = 'Please select your dates to continue.'
          this.submitting = false
          return
        }

        // Check if we already have an order token for this selection
        // Use optionId in key to distinguish
        const optKey = this.selectedOptionId || 'default'
        const storageKey = `order_${this.selectedOffering.key}_${optKey}_${datesToSubmit.join('_')}_${this.quantity}`
        const existingToken = sessionStorage.getItem(storageKey)

        if (existingToken) {
          // Try to fetch existing order
          try {
            const existingOrder = await api.orders.get(existingToken)
            if (existingOrder.status === 'pending' && !existingOrder.isExpired) {
              // Reuse existing order
              console.log('Reusing existing order:', existingToken)
              this.cartStore.setSelectedOffering(this.selectedOffering)
              this.cartStore.setSelectedOptionId(this.selectedOptionId)
              this.cartStore.setSelectedDates(datesToSubmit)
              this.cartStore.setSelectedLocation(this.locationName)
              this.cartStore.setQuantity(this.quantity)
              this.cartStore.setOrder(existingOrder)

              this.$router.push({
                name: 'Checkout',
                params: { orderToken: existingToken },
              })
              return
            }
          } catch (err) {
            // Order doesn't exist or errored, clear from storage and create new one
            console.log('Existing order not found, creating new one')
            sessionStorage.removeItem(storageKey)
          }
        }

        // Create order via API
        const order = await api.orders.create(
          this.selectedOffering.key,
          this.selectedOptionId, // Pass option ID
          datesToSubmit,
          this.quantity
        )

        // Store token in sessionStorage for reload recovery
        sessionStorage.setItem(storageKey, order.token)

        // Store order in cart store
        this.cartStore.setSelectedOffering(this.selectedOffering)
        this.cartStore.setSelectedOptionId(this.selectedOptionId)
        this.cartStore.setSelectedDates(datesToSubmit)
        this.cartStore.setSelectedLocation(this.locationName)
        this.cartStore.setQuantity(this.quantity)
        this.cartStore.setOrder(order)

        // Navigate to checkout with order token
        this.$router.push({
          name: 'Checkout',
          params: { orderToken: order.token },
        })
      } catch (err) {
        console.error('Failed to create order', err)

        if (err.response?.status === 404) {
          // Event closed or ended while user was on page
          this.eventEnded = true
        } else if (err.response?.status === 409) {
          // Extract per-date unavailability from structured response
          const responseData = err.response.data?.detail || err.response.data || {}
          const unavailDates = responseData.unavailableDates || []

          if (unavailDates.length > 0) {
            // Mark specific dates as unavailable
            for (const d of unavailDates) {
              if (!this.unavailableDates.includes(d)) {
                this.unavailableDates.push(d)
              }
            }
            const dateLabels = unavailDates.map(d => this.formatDate(d)).join(', ')
            if (unavailDates.length === 1) {
              this.error = `${dateLabels} doesn't have enough lockers for ${this.quantity}. Deselect it or reduce your quantity.`
            } else {
              this.error = `${unavailDates.length} dates don't have enough lockers for ${this.quantity}: ${dateLabels}. Deselect them or reduce your quantity.`
            }
          } else {
            this.error = 'Locker availability is limited \u2014 there aren\'t enough left to fulfill this request. Adjust your quantity or choose another option.'
          }
        } else if (err.response?.status === 400) {
          this.error = err.response.data?.detail || 'Please check your selection and try again.'
        } else {
          this.error = 'Unable to create your order. Please try again.'
        }
      } finally {
        this.submitting = false
      }
    },

    goBackToLocations() {
      const query = this.$route.query.preview ? { preview: this.$route.query.preview } : {}
      this.$router.push({
        name: 'EventLocations',
        params: { eventSlug: this.eventSlug },
        query,
      })
    },
  },
}
</script>

<template>
  <section class="section configure-locker">
    <CartEventHero />
    <div class="container">
      <!-- Back Navigation -->
      <div class="configure-header mb-4">
        <button type="button" class="button is-text is-small" @click="goBackToLocations">
          <b-icon icon="chevron-left" size="is-small" class="mr-1" />
          <span>Back to Locations</span>
        </button>
      </div>
      <div v-if="eventEnded" class="notification is-info is-light mb-4">
        <p class="has-text-weight-semibold mb-1">This event has ended</p>
        <p class="is-size-7">
          Locker reservations are no longer available.
          If you have an existing reservation, check your confirmation email for details.
        </p>
        <button type="button" class="button is-small is-info is-light mt-3" @click="goBackToLocations">
          <b-icon icon="chevron-left" size="is-small" class="mr-1" />
          Back to Event
        </button>
      </div>
      <div v-else-if="error" class="notification is-danger is-light mb-4">
        <button class="delete" @click="error = null"></button>
        {{ error }}
      </div>

      <div>
        <div v-if="loading" class="has-text-centered py-5">Loading…</div>

        <div v-else class="columns is-variable is-5">
          <div class="column is-12-tablet is-8-desktop">
            <div class="box configure-panel">
              <!-- Locker Size Selection -->
              <div class="configure-section mb-5">
                <h2 class="title is-5">Choose your locker size</h2>
                <p class="is-size-7 has-text-grey mb-3">
                  Select the locker size that works best for you.
                </p>

                <div v-if="locationOfferings.length === 0" class="notification is-warning">
                  No lockers available for this location.
                </div>

                <div v-else class="columns is-multiline is-mobile">
                  <div
                    v-for="offering in locationOfferings"
                    :key="offering.key"
                    class="column is-12-mobile is-6-tablet"
                  >
                    <div
                      class="card locker-card"
                      :class="{
                        'is-selected': offering.key === selectedOfferingKey,
                        'is-info-only': !isOfferingPurchasable(offering),
                        'is-sold-out': isSoldOut(offering),
                      }"
                      @click="!isSoldOut(offering) && selectOffering(offering.key)"
                    >
                      <div class="card-content">
                        <div class="is-flex is-justify-content-space-between is-align-items-start">
                          <p class="title is-6 mb-1">
                            {{ offering.displayName || 'Locker' }}
                          </p>
                          <span
                            v-if="isSoldOut(offering)"
                            class="tag is-danger is-small"
                          >
                            Sold Out
                          </span>
                          <span
                            v-else-if="!isOfferingPurchasable(offering)"
                            class="tag is-light is-info is-small"
                          >
                            Info Only
                          </span>
                        </div>
                        <p v-if="offering.description" class="is-size-7 has-text-grey-dark mb-2" style="white-space: pre-line">
                          {{ offering.description }}
                        </p>
                        <p v-if="isOfferingPurchasable(offering) && !isSoldOut(offering) && getDisplayPrice(offering)" class="is-size-7 has-text-weight-semibold">
                          <span
                            v-if="hasMultipleOptions(offering)"
                            class="has-text-grey is-size-7 mr-1"
                            >From</span
                          >
                          <span class="has-text-primary">
                            ${{ (getDisplayPrice(offering) / 100).toFixed(2) }}
                          </span>
                          <span v-if="!hasMultipleOptions(offering)" class="has-text-grey">
                            {{ getDisplayModeLabel(offering) }}
                          </span>
                        </p>
                        <p v-else-if="isOfferingPurchasable(offering) && !isSoldOut(offering) && getDisplayPrice(offering) === null" class="is-size-7 has-text-danger">
                          <b-icon icon="alert-circle" size="is-small" />
                          Price unavailable
                        </p>
                        <p v-if="isOfferingPurchasable(offering) && !isSoldOut(offering) && getDisplayPrice(offering) && getServiceFee(offering) > 0" class="is-size-7 has-text-grey-light">
                          (${{ (getBasePrice(offering) / 100).toFixed(2) }} + ${{ (getServiceFee(offering) / 100).toFixed(2) }} fee)
                        </p>
                        <p v-else-if="isSoldOut(offering)" class="is-size-7 has-text-danger">
                          No availability remaining
                        </p>
                        <p v-else-if="!isOfferingPurchasable(offering)" class="is-size-7 has-text-grey-dark">
                          Available on-site only
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Rental Type Selection -->
              <div
                v-if="selectedOffering && availableOptions.length > 1"
                class="configure-section mb-5"
              >
                <h2 class="title is-5">Select Rental Type</h2>
                <div class="columns is-mobile is-multiline">
                  <div v-for="opt in availableOptions" :key="opt.id" class="column is-6">
                    <div
                      class="card locker-card"
                      :class="{
                        'is-selected': opt.id === selectedOptionId,
                        'is-disabled': isOptionEffectivelySoldOut(opt),
                      }"
                      @click="!isOptionEffectivelySoldOut(opt) && selectOption(opt.id)"
                    >
                      <div class="card-content">
                        <p class="title is-6 mb-1">
                          {{
                            opt.label || (opt.id === 'full_event' ? 'Full Event' : 'Daily Rental')
                          }}
                          <span
                            v-if="getAvailabilityLabel(opt)"
                            class="tag is-small ml-2"
                            :class="{
                              'is-danger': isOptionEffectivelySoldOut(opt),
                              'is-warning': (!isOptionEffectivelySoldOut(opt) && opt.availability === 'sold_out') || opt.availability === 'almost_gone',
                              'is-info': opt.availability === 'limited',
                            }"
                          >
                            {{ getAvailabilityLabel(opt) }}
                          </span>
                        </p>
                        <p v-if="!isOptionEffectivelySoldOut(opt)" class="is-size-7 has-text-weight-semibold">
                          <span class="has-text-primary"
                            >${{ (getOptionDisplayPrice(opt) / 100).toFixed(2) }}</span
                          >
                          <span v-if="opt.pricingMode === 'fixed'" class="has-text-grey">
                            /event
                          </span>
                          <span v-else class="has-text-grey"> /day</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Date Selection -->
              <div v-if="selectedOffering" class="configure-section mb-5">
                <h2 class="title is-5">Select your dates</h2>
                <p class="is-size-7 has-text-grey mb-3">
                  <span v-if="pricingMode === 'fixed'"> Includes access for all event days. </span>
                  <span v-else>
                    Choose which days you need the locker.
                    <span
                      v-if="selectedDates.length === 0 && availableDates.length > 4"
                      class="has-text-info"
                    >
                      Tap to select dates.
                    </span>
                  </span>
                </p>

                <div class="date-grid">
                  <div
                    v-for="dateStr in availableDates"
                    :key="dateStr"
                    class="date-cell"
                    :class="{
                      'is-selected': isDateSelected(dateStr),
                      'is-unavailable': isDateUnavailable(dateStr),
                      'is-limited': getDateAvailability(dateStr) === 'limited' && !isDateUnavailable(dateStr),
                      'is-almost-gone': getDateAvailability(dateStr) === 'almost_gone' && !isDateUnavailable(dateStr),
                      'is-static': pricingMode === 'fixed',
                    }"
                    @click="pricingMode !== 'fixed' && (!isDateUnavailable(dateStr) || isDateSelected(dateStr)) && toggleDate(dateStr)"
                  >
                    <span class="date-cell__day">{{ formatDayAbbrev(dateStr) }}</span>
                    <span class="date-cell__date">{{ formatDateNum(dateStr) }}</span>
                    <span v-if="getDateScarcityLabel(dateStr)" class="date-cell__scarcity"
                      :class="{
                        'is-sold': getDateAvailability(dateStr) === 'sold_out',
                        'is-low': getDateAvailability(dateStr) === 'almost_gone',
                        'is-limited': getDateAvailability(dateStr) === 'limited',
                      }"
                    >{{ getDateScarcityLabel(dateStr) }}</span>
                  </div>
                </div>
              </div>

              <!-- Info-only Notice -->
              <div v-if="selectedOffering && !isAvailableForPurchase" class="configure-section mb-5">
                <b-notification type="is-info" :closable="false" class="info-only-notice">
                  <p class="has-text-weight-semibold mb-1">
                    <b-icon icon="information" size="is-small" class="mr-1" />
                    First Come, First Served
                  </p>
                  <p class="is-size-7" style="white-space: pre-line">
                    {{ selectedOffering.description || 'This locker option is not available for online purchase. It will be available on a first-come, first-served basis at the event.' }}
                  </p>
                </b-notification>
              </div>

              <!-- Quantity (only for purchasable products) -->
              <div v-if="isAvailableForPurchase" class="configure-section">
                <div class="configure-quantity">
                  <b-field label="Number of lockers">
                    <b-numberinput v-model="quantity" size="is-small" :min="1" :max="6" />
                  </b-field>
                  <p class="is-size-7 has-text-grey">You can reserve up to 6 lockers per order.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-12-tablet is-4-desktop">
            <!-- Pricing Error Notification -->
            <div v-if="hasPricingError" class="notification is-danger is-light mb-4">
              <p>
                <b-icon icon="alert-circle" size="is-small" class="mr-1" />
                <strong>Unable to display pricing.</strong> Please try refreshing the page or contact support.
              </p>
            </div>

            <OrderSummaryCard
              class="summary-card"
              :event-name="(currentEvent && currentEvent.name) || eventSlug"
              :location="locationName"
              :locker-name="selectedOffering ? (selectedOffering.displayName || selectedOffering.size || '') : ''"
              :dates="selectedDates"
              :quantity="quantity"
              :subtotal="hasPricingError ? 0 : displayTotalCents / 100"
              :total="hasPricingError ? 0 : displayTotalCents / 100"
              :service-fee="hasPricingError ? 0 : displayServiceFeeCents / 100"
              :show-rate="!hasPricingError"
              :rate="unitPriceCents ? unitPriceCents / 100 : 0"
              :rate-label="pricingMode === 'daily' ? 'Daily rate' : 'Rate'"
              :collapsible="false"
            >
              <p v-if="loadingPreview" class="is-size-7 has-text-grey mt-2 mb-3">
                <b-icon icon="loading" size="is-small" class="mdi-spin mr-1" />
                Calculating...
              </p>
              <p v-else-if="!hasPricingError" class="is-size-7 has-text-grey mt-2 mb-3">+ tax at checkout</p>
              <p v-else class="is-size-7 has-text-danger mt-2 mb-3">Pricing data unavailable</p>
              <div class="card-footer summary-card__footer" style="padding: 0; border-top: none">
                <div class="card-footer-item" style="border: none; padding: 0.75rem 0 0 0">
                  <b-button
                    type="is-primary"
                    expanded
                    :disabled="!canContinue"
                    :loading="submitting"
                    @click="continueToCheckout"
                  >
                    <span>Continue to Checkout</span>
                    <b-icon icon="arrow-right" size="is-small" class="ml-2" />
                  </b-button>
                </div>
              </div>
            </OrderSummaryCard>
          </div>

        </div>
      </div>
    </div>

    <!-- Sticky Footer (mobile only) -->
    <div v-if="selectedOffering && isAvailableForPurchase && !hasPricingError" class="sticky-footer is-hidden-tablet">
      <div class="sticky-footer__content">
        <div class="sticky-footer__summary">
          <span class="sticky-footer__total">${{ (displayTotalCents / 100).toFixed(2) }}</span>
          <span class="sticky-footer__label">{{ datesLabel }}</span>
        </div>
        <b-button
          type="is-primary"
          :disabled="!canContinue"
          :loading="submitting"
          @click="continueToCheckout"
        >
          Continue
          <b-icon icon="arrow-right" size="is-small" class="ml-1" />
        </b-button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.configure-locker {
  padding-top: 1.5rem;
}

.configure-header {
  margin-bottom: 1.75rem;
}

.configure-panel {
  padding: 1.5rem;
}

.locker-card {
  cursor: pointer;
  border-radius: 12px;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.locker-card.is-selected {
  box-shadow: 0 0 0 2px #2563eb;
  transform: translateY(-2px);
}

.locker-card.is-info-only {
  background-color: #f8fafc;
  border: 1px dashed #94a3b8;
  opacity: 0.85;
}

.locker-card.is-info-only.is-selected {
  box-shadow: 0 0 0 2px #64748b;
  border-color: #64748b;
}

.info-only-notice {
  border-radius: 8px;
}

.locker-card.is-sold-out {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  opacity: 0.7;
  cursor: not-allowed;
}

.locker-card.is-sold-out:hover {
  transform: none;
  box-shadow: none;
}

.summary-card {
  border-radius: 12px;
}

.summary-card__footer {
  margin-top: auto;
}

.date-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
  gap: 0.5rem;
}

/* Limit to 7 columns max on desktop for calendar week layout */
@media (min-width: 769px) {
  .date-grid {
    grid-template-columns: repeat(7, 1fr);
    max-width: 420px;
  }
}

.date-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 56px;
}

.date-cell:hover {
  border-color: #2563eb;
}

.date-cell__day {
  font-size: 0.65rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.date-cell__date {
  font-size: 1rem;
  font-weight: 600;
}

.date-cell__scarcity {
  font-size: 0.55rem;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.date-cell__scarcity.is-sold {
  color: #dc2626;
}

.date-cell__scarcity.is-low {
  color: #d97706;
}

.date-cell__scarcity.is-limited {
  color: #6b7280;
}

.date-cell.is-selected {
  background-color: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
}

.date-cell.is-selected .date-cell__day {
  color: #3b82f6;
}

.date-cell.is-unavailable {
  background-color: #fef2f2;
  border-color: #fecaca;
  cursor: not-allowed;
}

.date-cell.is-unavailable.is-selected {
  cursor: pointer;
}

.date-cell.is-static {
  cursor: default;
}

.date-cell.is-static:hover {
  border-color: #2563eb;
}

/* Sticky Footer */
.sticky-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.sticky-footer__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

.sticky-footer__summary {
  display: flex;
  flex-direction: column;
}

.sticky-footer__total {
  font-size: 1.1rem;
  font-weight: 600;
}

.sticky-footer__label {
  font-size: 0.75rem;
  color: #6b7280;
}

@media (min-width: 769px) {
  .configure-locker {
    padding-top: 2rem;
  }
}

/* Add bottom padding for sticky footer on mobile */
@media (max-width: 768px) {
  .configure-locker {
    padding-bottom: 5rem;
  }
}
</style>
